import { useCallback, useEffect, useState } from "react";
import { api, errMsg, fmtDate, fmtDateShort, M_PRIORITY, M_PRIORITY_STYLE, M_STATUS, M_STATUS_STYLE, M_TIPO, M_TIPO_STYLE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader, Spinner } from "@/components/bits";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import ManifestationFormDialog from "@/components/ManifestationFormDialog";
import ManifestationDetailDialog from "@/components/ManifestationDetailDialog";
import { ClipboardList, Eye, Loader2, Pencil, Plus, Search, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FILTERS = { q: "", tipo: "todos", status: "aberto", prioridade: "todas", date_from: "", date_to: "" };
const eqlLabel = (e) => [e?.numero, e?.descricao].filter(Boolean).join(" — ") || e?.id || "";

export default function ManifestationsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [areas, setAreas] = useState([]);
  const [team, setTeam] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [filterEql, setFilterEql] = useState(null);
  const [eqlFilterOpen, setEqlFilterOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    const params = {};
    if (filters.q.trim()) params.q = filters.q.trim();
    if (filters.tipo !== "todos") params.tipo = filters.tipo;
    if (filters.status !== "todos") params.status = filters.status;
    if (filters.prioridade !== "todas") params.prioridade = filters.prioridade;
    if (filters.date_from) params.date_from = filters.date_from;
    if (filters.date_to) params.date_to = filters.date_to;
    if (filterEql) params.eql_id = filterEql.id;
    api.get("/tenant/manifestacoes", { params }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [filters, filterEql]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const h = () => { setFilters({ ...EMPTY_FILTERS }); setFilterEql(null); };
    window.addEventListener("manifestacoes:reset-filters", h);
    return () => window.removeEventListener("manifestacoes:reset-filters", h);
  }, []);
  useEffect(() => {
    api.get("/tenant/areas").then(({ data }) => setAreas(data)).catch(() => {});
    api.get("/tenant/users").then(({ data }) => setTeam(data.filter((u) => u.role !== "client" && u.active))).catch(() => {});
  }, []);

  const deleteItem = async () => {
    setDeleting(true);
    try {
      await api.delete(`/tenant/manifestacoes/${deleteTarget.id}`);
      toast.success("Registro excluído");
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setDeleting(false);
    }
  };

  const hasFilters = filters.q || filters.tipo !== "todos" || filters.status !== "aberto"
    || filters.prioridade !== "todas" || filters.date_from || filters.date_to || filterEql;

  if (!items) return <Spinner />;

  return (
    <div data-testid="manifestacoes-page">
      <PageHeader title="Pedidos e Ocorrências" subtitle="Canal centralizado de solicitações, reclamações, sugestões e elogios — substitui o livro de ocorrências">
        <Button data-testid="new-manif-button" onClick={() => { setEditing(null); setFormOpen(true); }}
          className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-md transition-colors duration-200">
          <Plus className="h-4 w-4 mr-1.5" /> Novo Registro
        </Button>
      </PageHeader>

      <div className="bg-zinc-900 border border-zinc-800 rounded-md p-3 mb-4 space-y-3" data-testid="manif-filters">
        <div className="relative">
          <Search className="h-4 w-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
          <Input data-testid="manif-filter-q" value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            placeholder="Buscar por Nº, assunto, descrição ou solicitante…"
            className="pl-9 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-500 rounded-md focus:border-indigo-500" />
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-xs text-zinc-400">Tipo</Label>
            <Select value={filters.tipo} onValueChange={(v) => setFilters({ ...filters, tipo: v })}>
              <SelectTrigger data-testid="manif-filter-tipo" className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem data-testid="manif-filter-tipo-todos" value="todos">Todos</SelectItem>
                {Object.entries(M_TIPO).map(([k, label]) => (
                  <SelectItem key={k} data-testid={`manif-filter-tipo-${k}`} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Status</Label>
            <Select value={filters.status} onValueChange={(v) => setFilters({ ...filters, status: v })}>
              <SelectTrigger data-testid="manif-filter-status" className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem data-testid="manif-filter-status-todos" value="todos">Todos</SelectItem>
                {Object.entries(M_STATUS).map(([k, label]) => (
                  <SelectItem key={k} data-testid={`manif-filter-status-${k}`} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Prioridade</Label>
            <Select value={filters.prioridade} onValueChange={(v) => setFilters({ ...filters, prioridade: v })}>
              <SelectTrigger data-testid="manif-filter-prioridade" className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
                <SelectItem data-testid="manif-filter-prioridade-todas" value="todas">Todas</SelectItem>
                {Object.entries(M_PRIORITY).map(([k, label]) => (
                  <SelectItem key={k} data-testid={`manif-filter-prioridade-${k}`} value={k}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Abertura de</Label>
            <Input type="date" data-testid="manif-filter-from" value={filters.date_from}
              onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
              className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-40 [color-scheme:dark]" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">até</Label>
            <Input type="date" data-testid="manif-filter-to" value={filters.date_to}
              onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
              className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 rounded-md w-40 [color-scheme:dark]" />
          </div>
          <div>
            <Label className="text-xs text-zinc-400">Unidade (EQL)</Label>
            <button type="button" data-testid="manif-filter-eql-button" onClick={() => setEqlFilterOpen(true)}
              className="mt-1 flex items-center justify-between gap-2 w-44 h-9 px-3 text-sm bg-zinc-950 border border-zinc-800 rounded-md text-left hover:border-indigo-500/60 transition-colors duration-200">
              <span className={`truncate ${filterEql ? "text-zinc-100" : "text-zinc-500"}`}>{filterEql ? eqlLabel(filterEql) : "Todas"}</span>
              {filterEql ? (
                <X data-testid="manif-filter-eql-clear" className="h-3.5 w-3.5 text-zinc-500 hover:text-rose-400 shrink-0"
                  onClick={(e) => { e.stopPropagation(); setFilterEql(null); }} />
              ) : (
                <Search className="h-3.5 w-3.5 text-zinc-500 shrink-0" />
              )}
            </button>
          </div>
          {hasFilters && (
            <Button variant="ghost" data-testid="manif-filter-clear"
              onClick={() => { setFilters(EMPTY_FILTERS); setFilterEql(null); }}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
              Limpar filtros
            </Button>
          )}
        </div>
      </div>

      {items.length === 0 ? (
        <EmptyState icon={ClipboardList} title="Nenhum registro encontrado"
          subtitle="Registre pedidos e ocorrências dos moradores para centralizar a comunicação com a administração." />
      ) : (
        <div className="bg-zinc-900 border border-zinc-800 rounded-md overflow-x-auto">
          <table className="w-full text-xs min-w-[900px]">
            <thead>
              <tr className="border-b border-zinc-800 text-left">
                <th className="px-3 py-2.5 text-[11px] font-medium text-zinc-400">Nº</th>
                <th className="px-3 py-2.5 text-[11px] font-medium text-zinc-400">Tipo</th>
                <th className="px-3 py-2.5 text-[11px] font-medium text-zinc-400">Assunto</th>
                <th className="px-3 py-2.5 text-[11px] font-medium text-zinc-400">Solicitante</th>
                <th className="px-3 py-2.5 text-[11px] font-medium text-zinc-400">Unidade</th>
                <th className="px-3 py-2.5 text-[11px] font-medium text-zinc-400">Status</th>
                <th className="px-3 py-2.5 text-[11px] font-medium text-zinc-400">Responsável</th>
                <th className="px-3 py-2.5 text-[11px] font-medium text-zinc-400">Abertura</th>
                <th className="px-3 py-2.5 text-[11px] font-medium text-zinc-400">Conclusão</th>
                <th className="px-3 py-2.5 text-[11px] font-medium text-zinc-400 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {items.map((m) => (
                <tr key={m.id} data-testid={`manif-row-${m.id}`}
                  className="border-b border-zinc-800/60 hover:bg-zinc-800/50 transition-colors duration-200">
                  <td className="px-3 py-2.5 font-mono text-xs text-zinc-400">#{String(m.number).padStart(4, "0")}</td>
                  <td className="px-3 py-2.5"><Badge className={`${M_TIPO_STYLE[m.tipo]} border-0 text-[11px] font-medium`}>{M_TIPO[m.tipo]}</Badge></td>
                  <td className="px-3 py-2.5 text-zinc-100 max-w-56 truncate" data-testid={`manif-assunto-${m.id}`} title={m.assunto}>{m.assunto}</td>
                  <td className="px-3 py-2.5 text-zinc-300 text-xs max-w-40 truncate">{m.is_anonimo ? "Anônimo" : m.solicitante_nome}</td>
                  <td className="px-3 py-2.5 text-zinc-400 text-xs max-w-28 truncate">{m.eql_label ? m.eql_label.split(" — ").pop() : "—"}</td>
                  <td className="px-3 py-2.5 cursor-pointer" onClick={() => setViewing(m.id)} title="Clique para visualizar">
                    <Badge className={`${M_STATUS_STYLE[m.status]} border-0 text-[11px] font-medium hover:brightness-125 transition`} data-testid={`manif-status-${m.id}`}>{M_STATUS[m.status]}</Badge>
                  </td>
                  <td className="px-3 py-2.5 text-zinc-400 text-xs max-w-32 truncate">{m.responsavel_name || "—"}</td>
                  <td className="px-3 py-2.5 text-zinc-400 text-xs whitespace-nowrap">{m.created_at ? new Date(m.created_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" }) : "—"}</td>
                  <td className="px-3 py-2.5 text-zinc-400 text-xs whitespace-nowrap">{fmtDate(m.data_conclusao)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <span className="inline-flex items-center gap-1">
                      <Button data-testid={`manif-view-${m.id}`} variant="ghost" size="icon" title="Visualizar"
                        onClick={() => setViewing(m.id)}
                        className="h-8 w-8 text-zinc-400 hover:text-indigo-400 hover:bg-zinc-800">
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button data-testid={`manif-edit-${m.id}`} variant="ghost" size="icon" title="Editar"
                        onClick={() => { setEditing(m); setFormOpen(true); }}
                        className="h-8 w-8 text-zinc-400 hover:text-indigo-400 hover:bg-zinc-800">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {user?.role === "tenant_admin" && (
                        <Button data-testid={`manif-delete-${m.id}`} variant="ghost" size="icon" title="Excluir"
                          onClick={() => setDeleteTarget(m)}
                          className="h-8 w-8 text-zinc-400 hover:text-rose-400 hover:bg-zinc-800">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ManifestationFormDialog open={formOpen} onOpenChange={setFormOpen} editing={editing} areas={areas} onSaved={load} />
      <ManifestationDetailDialog mid={viewing} team={team} onClose={() => setViewing(null)} onChanged={load} />

      <Dialog open={eqlFilterOpen} onOpenChange={setEqlFilterOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100" data-testid="manif-filter-eql-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Filtrar por EQL</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Selecione a unidade para listar os registros vinculados a ela.
            </DialogDescription>
          </DialogHeader>
          <SearchableSelect onSelect={(eql) => { setFilterEql(eql); setEqlFilterOpen(false); }} />
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
          <DialogHeader>
            <DialogTitle className="text-lg font-medium">Excluir registro</DialogTitle>
            <DialogDescription className="text-sm text-zinc-400">
              Tem certeza que deseja excluir <span className="text-zinc-200 font-medium">#{String(deleteTarget?.number || 0).padStart(4, "0")} — {deleteTarget?.assunto}</span>? A exclusão fica registrada na auditoria.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end gap-2" data-testid="delete-manif-dialog">
            <Button data-testid="delete-manif-cancel" variant="ghost" onClick={() => setDeleteTarget(null)}
              className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 rounded-md">
              Cancelar
            </Button>
            <Button data-testid="delete-manif-confirm" onClick={deleteItem} disabled={deleting}
              className="bg-rose-600 hover:bg-rose-500 text-white rounded-md transition-colors duration-200">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Excluir"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
